import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Eye, EyeOff, Save } from "lucide-react";
import { toast } from "sonner";
import AccountLayout from "../components/AccountLayout";
import MailingAddresses from "../components/MailingAddresses";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function MyProfile() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const [stats, setStats] = useState({ total: 0, lifetime_spend: 0, active: 0, delivered: 0 });

  useEffect(() => {
    if (user) setName(user.name || "");
  }, [user]);

  useEffect(() => {
    api.get("/orders").then((r) => {
      const orders = r.data;
      const delivered = orders.filter((o) => o.status === "delivered" && o.payment_status === "paid");
      const active = orders.filter((o) => o.status !== "delivered" && o.payment_status === "paid");
      const spend = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + o.total, 0);
      setStats({ total: orders.length, lifetime_spend: spend, active: active.length, delivered: delivered.length });
    }).catch(() => {});
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error("Name too short"); return; }
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { name: name.trim() });
      toast.success("PROFILE UPDATED");
    } catch (err) {
      toast.error("FAILED", { description: err?.response?.data?.detail || "" });
    } finally { setSavingProfile(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("New password must be 6+ chars"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("PASSWORD CHANGED");
      setCurrentPw(""); setNewPw("");
    } catch (err) {
      toast.error("FAILED", { description: err?.response?.data?.detail || "" });
    } finally { setSavingPw(false); }
  };

  return (
    <AccountLayout>
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="profile-stats">
        {[
          { label: "TOTAL ORDERS", value: stats.total },
          { label: "LIFETIME SPEND", value: `₹${stats.lifetime_spend.toFixed(0)}` },
          { label: "ACTIVE ORDERS", value: stats.active },
          { label: "DELIVERED", value: stats.delivered },
        ].map((s) => (
          <div key={s.label} className="border border-[#27272A] bg-[#0A0A0A] p-4">
            <p className="label-mono text-[#71717A]">{s.label}</p>
            <p className="font-display font-black text-2xl mt-2 text-[#CFF048]">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Two cards side-by-side */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile form */}
        <form onSubmit={saveProfile} className="bg-[#0A0A0A] border border-[#27272A] p-6" data-testid="profile-form">
          <p className="label-mono text-[#CFF048]">// PROFILE</p>
          <h2 className="font-display font-black text-2xl uppercase mt-2">Edit Details</h2>

          <div className="mt-6 space-y-4">
            <div>
              <p className="label-mono text-[#71717A] mb-2">FULL NAME</p>
              <input className="cult-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
            </div>
            <div>
              <p className="label-mono text-[#71717A] mb-2">EMAIL (READ-ONLY)</p>
              <input className="cult-input opacity-60 cursor-not-allowed" value={user?.email || ""} readOnly />
            </div>
            <div>
              <p className="label-mono text-[#71717A] mb-2">MEMBER SINCE</p>
              <p className="font-mono-cult text-sm">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</p>
            </div>
          </div>
          <button type="submit" disabled={savingProfile} className="btn-neon mt-6 w-full" data-testid="save-profile-btn">
            <Save size={16} /> {savingProfile ? "SAVING..." : "SAVE CHANGES"}
          </button>
        </form>

        {/* Password change */}
        <form onSubmit={changePw} className="bg-[#0A0A0A] border border-[#27272A] p-6" data-testid="change-password-form">
          <p className="label-mono text-[#CFF048]">// SECURITY</p>
          <h2 className="font-display font-black text-2xl uppercase mt-2">Change Password</h2>

          <div className="mt-6 space-y-4">
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                required value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="CURRENT PASSWORD"
                className="cult-input pr-12"
                data-testid="current-password"
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#CFF048]">
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                required value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="NEW PASSWORD (6+ CHARS)"
                className="cult-input pr-12"
                data-testid="new-password"
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#CFF048]">
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={savingPw} className="btn-neon mt-6 w-full" data-testid="change-password-btn">
            {savingPw ? "UPDATING..." : "UPDATE PASSWORD"}
          </button>
        </form>
      </div>

      {/* Quick links */}
      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <Link to="/account/orders" className="border border-[#27272A] hover:border-[#CFF048] p-5 flex items-center justify-between group">
          <div>
            <p className="label-mono text-[#71717A]">// ACTIVE</p>
            <p className="font-display font-bold mt-1">My Orders</p>
          </div>
          <Package size={20} className="text-[#CFF048] group-hover:scale-110 transition-transform" />
        </Link>
        <Link to="/account/past-orders" className="border border-[#27272A] hover:border-[#CFF048] p-5 flex items-center justify-between group">
          <div>
            <p className="label-mono text-[#71717A]">// HISTORY</p>
            <p className="font-display font-bold mt-1">Past Orders</p>
          </div>
          <Package size={20} className="text-[#CFF048] group-hover:scale-110 transition-transform" />
        </Link>
      </div>

      {/* Mailing addresses */}
      <MailingAddresses />
    </AccountLayout>
  );
}
